import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Clock, Info, LogIn } from "lucide-react";
import { api, errorMessage, isApiError, safeNext } from "../api.js";
import { PasswordInput } from "../components/PasswordFields.js";
import { Button } from "../components/ui/Button.js";
import { Field, Input } from "../components/ui/Form.js";
import { Callout } from "../components/ui/States.js";
import { fmtCountdown } from "../lib/format.js";
import { useDocumentTitle, useNow } from "../lib/hooks.js";
import { qk } from "../queries.js";
import { AuthLayout } from "./AuthLayout.js";

/** Sin Retry-After, se espera un minuto antes de permitir otro intento. */
const DEFAULT_BLOCK_SECONDS = 60;

export function LoginPage() {
  useDocumentTitle("Ingresar");
  const [params] = useSearchParams();
  const rawNext = params.get("next");
  const next = safeNext(rawNext);
  const location = useLocation();
  const prefillEmail = (location.state as { email?: string } | null)?.email ?? "";
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const now = useNow(blockedUntil ? 1000 : null);
  const remaining = blockedUntil ? Math.max(0, Math.ceil((blockedUntil - now) / 1000)) : 0;
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (blockedUntil && remaining === 0) {
      setBlockedUntil(null);
      setError(null);
    }
  }, [blockedUntil, remaining]);

  useEffect(() => {
    (prefillEmail ? passwordRef : emailRef).current?.focus();
  }, [prefillEmail]);

  const login = useMutation({
    mutationFn: api.auth.login,
    onSuccess: (me) => {
      qc.setQueryData(qk.me, me);
      void navigate(next, { replace: true });
    },
    onError: (err) => {
      setPassword("");
      if (isApiError(err, 429)) {
        setBlockedUntil(Date.now() + (err.retryAfterSeconds ?? DEFAULT_BLOCK_SECONDS) * 1000);
        setError(null);
      } else if (isApiError(err, 401) || isApiError(err, 400)) {
        // Mensaje genérico a propósito: no revela si el correo existe.
        setError("Correo o contraseña incorrectos.");
      } else {
        setError(errorMessage(err));
      }
      passwordRef.current?.focus();
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (blockedUntil || login.isPending) return;
    if (!email.trim() || !password) {
      setError("Ingresa tu correo y tu contraseña.");
      return;
    }
    setError(null);
    login.mutate({ email: email.trim(), password });
  }

  const blocked = remaining > 0;

  return (
    <AuthLayout title="Ingresar a SENTINEL" subtitle="Usa el correo y la contraseña de tu cuenta institucional.">
      {rawNext && !error && !blocked && (
        <Callout tone="info" icon={<Info />} className="mb-4">
          Tu sesión expiró o aún no has ingresado. Ingresa para continuar.
        </Callout>
      )}
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field label="Correo electrónico" required>
          {(p) => (
            <Input
              {...p}
              ref={emailRef}
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
              placeholder="nombre@aseguradora.cl"
            />
          )}
        </Field>
        <Field label="Contraseña" required>
          {(p) => (
            <PasswordInput
              {...p}
              ref={passwordRef}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={1024}
            />
          )}
        </Field>

        {error && (
          <p role="alert" className="text-[13px] font-medium text-danger">
            {error}
          </p>
        )}
        {blocked && (
          <Callout tone="warning" icon={<Clock />} title="Demasiados intentos">
            Por seguridad, el ingreso quedó bloqueado temporalmente. Podrás intentarlo de nuevo en{" "}
            <strong className="tabular-nums" aria-live="off">
              {fmtCountdown(remaining)}
            </strong>
            .
          </Callout>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={login.isPending}
          disabled={blocked}
          icon={<LogIn className="size-4" aria-hidden="true" />}
        >
          {blocked ? `Espera ${fmtCountdown(remaining)}` : "Ingresar"}
        </Button>
      </form>
      <p className="mt-5 text-[12.5px] text-subtle">
        ¿Olvidaste tu contraseña? Pide a un administrador de SENTINEL que la restablezca: te entregará un enlace
        para definir una nueva.
      </p>
    </AuthLayout>
  );
}
